import * as Yup from 'yup';

export const INITIAL_VALUES = {
  // same shape as before
  organization: '', abn: '', address: '', contact_first_name: '',
  contact_last_name: '', phone: '', email: '', present_first_name: '',
  present_last_name: '',
  invoice_contact_option: 'same_as_contact',
  invoice_first_name: '', invoice_last_name: '', invoice_email: '',
  payment_option: 'upfront',
  facility_id: '',
  location_id: '',
  area_ids: [],
  sub_area_ids: [],
  sub_area_zone_ids: [],
  hiring_purpose: '', equipment_required: '',
  spectators: 0, total_attendance: 1,
  is_charging_attendance: '0',
  insurance_certificate: null,
  start_date: null, end_date: null,
  sessions: [
    {
      day: '',
      start_time: '',
      end_time: ''
    }
  ],
  continues_school_holidays: 'continues_school_holidays_no',
  has_booking_exceptions: 'has_booking_exceptions_no',
  booking_exception_dates: [],
  additional_notes: '',
  termsCheck: false,
  signed_name: '',
  signed_ip_address: '',
  signature: '',
  recaptcha_token: '',
};

export const validationHireAgreementSchema = Yup.object({
  organization: Yup.string().required('Required'),
  address: Yup.string().required('Required'),
  contact_first_name: Yup.string().required('Required'),
  contact_last_name: Yup.string().required('Required'),
  phone: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  present_first_name: Yup.string().required('Required'),
  present_last_name: Yup.string().required('Required'),

  invoice_contact_option: Yup.string().required('Required'),
  invoice_first_name: Yup.string().when('invoice_contact_option', {
    is: 'different_contact',
    then: Yup.string().required('Required'),
    otherwise: Yup.string(),
  }),
  invoice_last_name: Yup.string().when('invoice_contact_option', {
    is: 'different_contact',
    then: Yup.string().required('Required'),
    otherwise: Yup.string(),
  }),
  invoice_email: Yup.string()
    .email('Invalid email')
    .when('invoice_contact_option', {
      is: 'different_contact',
      then: Yup.string().required('Required'),
      otherwise: Yup.string(),
    }),

  payment_option: Yup.string().required('Required'),


  hiring_purpose: Yup.string().required('Required'),
  equipment_required: Yup.string(),

  spectators: Yup.number().min(0),
  total_attendance: Yup.number().min(1).required('Required'),
  is_charging_attendance: Yup.string().required('Required'),
  insurance_certificate: Yup.mixed(),

  start_date: Yup.date().nullable().required('Required'),
  end_date: Yup.date()
    .nullable()
    .min(Yup.ref('start_date'), 'End date cannot be before start date')
    .required('Required'),

  // Multiple Sessions
  sessions: Yup.array()
    .of(
      Yup.object().shape({
        day: Yup.string().required('Day is required'),
        start_time: Yup.string().required('Start time is required'),
        end_time: Yup.string()
          .required('End time is required')
          .test(
            'is-after-start',
            'End time must be after start time',
            function validateEndTimeAfterStartTime(value) {
              const { start_time } = this.parent;
              if (!start_time || !value) return true;
              return value > start_time;
            }
          )
      })
    )
    .min(1, 'At least one session must be defined'),

  continues_school_holidays: Yup.string().required('Required'),
  has_booking_exceptions: Yup.string().required('Required'),

  additional_notes: Yup.string(),
  termsCheck: Yup.boolean().oneOf([true], 'Must accept Terms'),
  signed_name: Yup.string().when('$editMode', {
    is: false,
    then: Yup.string().required('Full name of person signing is required'),
    otherwise: Yup.string(),
  }),
  signed_ip_address: Yup.string(),
  signature: Yup.string().when('$editMode', {
    is: false,
    then: Yup.string().test('signature-required', 'Signature is required', function(value) {
      if (!value || value === '') {
        return false;
      }
      // If it's a data URL, check if it's not just an empty canvas
      if (value.startsWith('data:image/')) {
        // A completely empty canvas data URL would be very short
        return value.length > 100; // Empty canvas data URLs are typically around 22-50 characters
      }
      return true;
    }),
    otherwise: Yup.string(),
  }),

  recaptcha_token: Yup.string().when('$editMode', {
    is: false,
    then: Yup.string().required('Please complete the captcha verification'),
    otherwise: Yup.string(),
  }),
});


export const termsAndConditions = [
  {
    title: "Interpretation",
    clauses: [
      { text: `"Applicable policy" means the policy governing the use of the Facilities as determined by the Centre Manager from time to time including but not limited to Conditions of Entry and Terms of Use;` },
      { text: `"BlueFit Pty Ltd", and or "Facility Name" means the land and improvements on "Facility Address";` },
      { text: `"Centre Manager" includes authorised and or designated officers of BlueFit;` },
      { text: `"Facilities" means venue, fixtures, fittings, equipment and services specified on the Hire Agreement form;` },
      { text: `"Hire Agreement" form and "Conditions for Hire of Facilities" form means the form so described;` },
      { text: `"Hirer" means person / organization referred to on the Hire Agreement form;` },
      { text: `Hirer’s Responsible Person is the contact person of the Hirer, as nominated by the Hirer in the Hire Agreement Form or the acting on behalf of the hirer in the Hiring of the Facilities.` },
      { text: `"Hiring charge" means the sum payable in accordance with Clause 3;` },
      { text: `"Hiring period" means the period / periods specified on the Hire Agreement form;` },
      { text: `"Hiring purpose" means the purpose specified on the Hire Agreement form; and` },
      { text: `"Hire" means the event or booking specified on the Hire Agreement form.` },
      { text: `"Opening hours" means the hours in which the facility is open to the public as listed on the "Facility Name" website;` }
    ]
  },
  {
    title: "Agreement for Hire",
    clauses: [
      { text: `Subject to the payment of monies by the Hirer to BlueFit as specified in these Conditions for Hire of Facilities, BlueFit will hire the Facilities to the Hirer for the hiring purpose and hiring period specified; and` },
      { text: `These Conditions for Hire of Facilities and the Hire Agreement forms are the entire agreement between the parties.` }
    ]
  },
  {
    title: "Fees and Charges",
    clauses: [
      { text: `If the hiring charge is described as fixed, the amount is the hiring charge payable;` },
      { text: `If the hiring charge is described as indicative, the amount shown is an estimate of the hiring charge payable which may be adjusted to reflect the hire requirements in accordance with rates determined from time-to-time by the Centre Manager;` },
      { text: `On-Costs: The Hirer agrees to pay all on-costs for the hire.  The indicative on-costs items are based on the information provided by the Hirer on the Hire Agreement form.  If those indicative on-costs are exceeded, the Hirer is liable to pay all on-costs incurred; and` },
      { text: `The Hirer agrees to make payment within 7 days of receipt of a valid tax invoice from BlueFit;` },
      { text: `Any bookings outside of the "Facility Name" opening hours will incur additional charges;` },
      { text: `Where required by BlueFit, the Hirer will pay a deposit of 10% of the Hire Charge. The deposit will be payable at the time of making the booking and where cancelled by the Hirer, be non-refundable.` }
    ]
  },
  {
    title: "Cancellation by Hirer",
    clauses: [
      { text: `The Hirer may cancel the Hire by giving the Centre Manager notice in writing of intention to cancel, subject to 4.1 and 4.2 herein;` },
      { text: `If at least 60 days before the hiring period, the Hirer cancels, BlueFit will invoice the Hirer for any expenses incurred incidental to the Hire; and` },
      { text: `If the purpose of Hire is school sports-related, the Hirer may cancel the Hire by giving the Centre Manager no less than twenty-four (24) hours notice in writing (via email) of intention to cancel—failure to do so will incur a seventy-five dollar ($75.00), inclusive of GST, late notice cancellation fee, waived if a replacement booking is secured.` }
    ]
  },
  {
    title: "Cancellation or Re-Allocation by Centre Management",
    clauses: [
      {
        text: `The Centre Manager may cancel the hire or re-allocate the hired Facilities if, in their opinion:`,
        subClauses: [
          { text: `The Facilities will be unfit for use or unduly damaged by use during the hiring period;` },
          { text: `The Hirer has failed to comply with these Conditions for Hire of Facilities;` },
          { text: `A major event, requires the facility; and` },
          { text: `The Hirer is not making complete use of the hired Facilities.` }
        ]
      },
      { text: `Where the "Facility Name" is required by either local or state government as a result of a major emergency or natural disaster within the area, the emergency shall take precedence and your booking shall be cancelled. In these circumstances, no compensation shall be payable to the Hirer.` }
    ]
  },
  {
    title: "Hirer's Obligations",
    clauses: [
      {
        text: `The Hirer will:`,
        subClauses: [
          { text: `Maintain compulsory work health and safety insurance, public liability insurance and any other insurance as required by BlueFit;` },
          { text: `Cover all of the activities of the Hirer and its contractors and sub-contractors within their applicable insurance policies for the hiring period;` },
          { text: `produce all applicable certificates of currency and policies to BlueFit within 5 days;` },
          { text: `Must maintain Work Health and Safety Insurance in the amount as required by law;` },
          { text: `Obtain all necessary consents from all persons interested in the copyright or performing rights of any matter used by the Hirer;` },
          { text: `Furnish the Centre Manager with an Electrical Equipment Register if electrical appliances are brought onto the facility, whether by the Hirer or its contractors, sub-contractors or guests;` },
          { text: `Pay such further charges as determined by the Centre Manager on demand if any part of the "Facility Name" other than the Facilities hired are used by the Hirer; or the Facilities hired, are used by the Hirer outside the hiring period;` },
          { text: `Obey any and all instructions given by the Centre Manager and/or staff as to use of, and access to and from, the Facilities;` },
          { text: `Refer all injured, ill or incapacitated parties to the lifeguard on duty for treatment;` },
          { text: `Assist the Centre Manager in any way possible when evacuating participants, officials or guests;` },
          { text: `Ensure that the Hirers Responsible Person be present at all times (per Hire Agreement Form);` },
          { text: `Supervise and control all participants and officials and restrict participants to areas designated by the Centre Manager for their use;` },
          { text: `Leave the Facilities, changing rooms, toilets and showers in a clean and tidy condition;` },
          { text: `Immediately after the hiring period the Hirer must ensure all goods brought into the "Facility Name" for the hiring purpose are removed.  The Centre Manager may, without liability for loss or damage, remove any such goods from the "Facility Name" as deemed appropriate; and` },
          { text: `Where the Centre Manager agrees to allow storage of the Hirer’s or others’ goods prior to, during or after the hire period, the Centre Manager provides no warranties as to the security provided for such storage and the Hirer indemnifies the Centre Manager in respect of any loss of, damage to, the goods.` },
          { text: `Warrant that the Hirer and its personnel, hold current Working With Children’s Check’s and suitable qualifications and accreditations for the services provided (if applicable) and will produce copies of the same to BlueFit where requested. The hirer must supply BlueFit all information to allow the Licensor to satisfy its ‘Working with Children – Child Protection’ obligations, whatever they may be.` },
          { text: `Ensure all Hirer’s personnel where uniform that identifies the Hirer’s business (where applicable);` },
          { text: `Use any membership card that relates to the provisions of the Hirer's services only for attendance of a "Facility Name" with a client and not use the same for personal access to the Centre.;` },
          { text: `Where applicable, ensure that any of the Hirer's clients pay the associated entry fee of the facility or use a valid membership to access the same.;` },
          { text: `Not undertake personal training or swimming lessons unless express written consent is provided by BlueFit or Centre Management.;` }
        ]
      },
      {
        text: `The Hirer will not, without the approval in writing of the Centre Manager, within or adjacent to the "Facility Name":`,
        subClauses: [
          { text: `Use the Facilities for any purpose other than the hiring purpose;` },
          { text: `Allow any person not subject to the direction and control of the Hirer to use the Facilities;` },
          { text: `Hawk, sell, dispose of or supply anything whatsoever, or do so contrary to any condition imposed by the Centre Manager;` },
          { text: `Bring, or permit to be brought, any animal (save for those as set out in the Conditions of Entry), alcoholic beverage, or dangerous goods;` },
          { text: `Re-hire the Facilities to another person/organization;` },
          { text: `Use any part other than the Facilities booked;` },
          { text: `Alter, move or remove any fixture, fitting or furnishing;` },
          { text: `Erect or display any advertisement or do so contrary to any conditions imposed by the Centre Manager;` },
          { text: `Take any collection;` },
          { text: `Conduct any game of chance, or mixed chance and skill, lottery, or sweep-stake;` },
          { text: `Bet or wager, or permit any person to bet or wager;` },
          { text: `Erect any marquee, hut, stall or similar structure contrary to conditions imposed by the Centre Manager;` },
          { text: `Sell, offer or permit to be sold, offered or exposed for sale food, beverage, refreshment or other goods or service.;` }
        ]
      }
    ]
  },
  {
    title: "Fitness for Hiring",
    clauses: [
      { text: `The Hirer agrees that they are satisfied that, the Facilities are fit for hiring and acknowledges that BlueFit does not warrant that the Facilities are fit for the hiring purpose.  The Hirer must obtain any necessary permits, or authorities required for its use of the Facilities and uses the Facilities at its own risk.` }
    ]
  },
  {
    title: "Conditions of Entry",
    clauses: [
      {
        text: `The Hirer, the Hirer's participants, officials, contractors and sub-contractors agree to obey any and all Conditions of Entry as provided by the "Facility Name", including but not limited to the following:`,
        subClauses: [
          { text: `The "Facility Name" is a non-smoking venue;` },
          { text: `Please observe pool and gymnasium rules at all times, including showering before entering the pools;` },
          { text: `Any patrons under the influence of alcohol or drugs will not be permitted into the Centre;` },
          { text: `Anyone attempting to gain entry without paying or without authorisation will be prosecuted. No pass outs will be issued;` },
          { text: `Patrons are not permitted to bring glass objects, knives or alcohol into the Centre. Patron's bags may be searched;` },
          { text: `Abusive, disruptive or offensive behavior and language are not permitted. Offenders will be removed from the Centre;` },
          { text: `Children under the age of 6 years must be closely supervised (within arm’s reach at all times) by a responsible adult (16 years and over), you may be asked to leave the Centre should supervision of your child not be sufficient;` },
          { text: `Lifeguards are not baby sitters—the safety of your children is your responsibility;` },
          { text: `For pools access, articles of clothing such as street wear, underwear, cut-off jeans, t-shirts, bike-pants, leotards, casual wear and sporting wear are strictly prohibited. Only recognised swimwear, made from lycra and nylon is to be worn in the water. Only recognised waterproof nappies are to be worn in the water for infants;` },
          { text: `For gymnasium or group fitness class room access, all patrons are to wear appropriate and non-offensive attire. The wearing of fluro work shirts will not be permitted;` },
          { text: `Standing and storage of belongings on seats and tables is not permitted;` },
          { text: `"Facility Name" caters for all food and beverage requirements, for self catered functions please contact management;` },
          { text: `No pets or animals permitted in the Centre, with the exception of Guide Dogs;` },
          { text: `BlueFit staff only are permitted to conduct learn to swim, private swimming lessons, squad coaching and fitness sessions;` },
          { text: `Cameras and mobile phone cameras are not permitted in change rooms;` },
          { text: `"Facility Name" accepts no responsibility for lost or stolen belongings. Patrons are advised not to bring phones, cameras or wallets to the Centre;` },
          { text: `Management reserves the right to refuse entry—refusal to comply with these Conditions of Entry may result in removal from the Centre, repeat offenders will be suspended;` },
          { text: `If your child requires a floatation aid while swimming, we strongly advise that the parent or carer is in the water and within arms reach of the child at all times;` },
          { text: `In the interest of hygiene for all patrons, spitting is not permitted within the Centre;` },
          { text: `Please be familiar with the Centre’s Pool Rules: running, bomb diving, back or front flips, touching the lane ropes or flags, littering, or unruly behavior is not permitted;` },
          { text: `Chewing/bubble gum, coloured zinc or hairspray, coloured body paint, balloons, smoke machines, hard balls of any description are not permitted within the Centre;` },
          { text: `Please obey the Pool Lifeguard’s and Centre staff’s directions at all times; and` },
          { text: `Please do not block the Centre’s emergency exits with items such as garden furniture, strollers, bags and clothing.  For emergency management, these areas must remain clear of obstructions at all times;` },
          { text: `The reasonable direction provided by BlueFit and/or Centre Management as to the manner and frequency of the cleaning of the Facilities.` }
        ]
      }
    ]
  },
  {
    title: "Damage to the \"Facility Name\"",
    clauses: [
      { text: `The Hirer agrees to pay BlueFit, on demand, the cost of repairing or making good any damage to the "Facility Name", or the loss of any equipment arising out of, or incidental to the hiring, the actions of the hirer and/or their patrons. The Hirer will not be liable for damage caused on account of events outside the reasonable control or direction of the Hirer.` }
    ]
  },
  {
    title: "Exclusion of Liability",
    clauses: [
      { text: `BlueFit will not be liable for any loss or damage caused directly or indirectly by any fault or failure of any of the Services/utilities supply, or supply of any other power or energy to the "Facility Name", where such fault or failure is not the responsibility of BlueFit.` }
    ]
  },
  {
    title: "Public Liability Insurance",
    clauses: [
      {
        text: `Incorporated bodies, state schools, non-state schools, sporting clubs/associations and commercial groups must provide evidence of their current public liability insurance with an insurance underwriter registered to transact insurance business under Commonwealth law. A Certificate of Currency in respect of the activity, function, event, for public liability coverage of not less than $20 Million dollars must be provided to the Centre Manager with a signed and completed Hire Agreement Form, no less than seven days prior to the activity, function, event.`,
        subClauses: [
          { text: `Noting BlueFit for its respective rights and interests (FTRR&I);` },
          { text: `Imply liability for third party compensation rights and entitlements.` }
        ]
      }
    ]
  },
  {
    title: "Indemnity and Release",
    clauses: [
      { text: `The Hirer enters, occupies and uses the "Facility Name" and Facilities at its own risk.` },
      {
        text: `The Hirer, during the Hire Period, is liable for and indemnifies BlueFit from and against all liability, loss, costs and expenses arising from or incurred in connection with:`,
        subClauses: [
          { text: `damage, loss, injury or death within or outside the Facilities or "Facility Name" to the extent caused or contributed to by the act, omission, negligence or default of the Hirer, the Hirer’s Employees and Agents or the Hirer’s invitees;` },
          { text: `the Hirer’s default under this agreement; and` },
          { text: `BlueFit doing anything which the Hirer must do under this agreement but has not done in accordance with its obligations under this agreement,` },
          { text: `except to the extent that the damage, loss or injury is caused by the act, omission, negligence or default of BlueFit, its Employees and Agents. The hirer’s liability will be proportionally reduced to the extent damage, loss or injury was directly caused by an act, omission, negligence or default of BlueFit, its Employees or Agents.` }
        ]
      },
      {
        text: `The Hirer releases BlueFit, the BlueFit’s Employees and Agents from, and agrees that BlueFit the BlueFit’s Employees and Agents are not liable for any liability, loss, costs and expenses arising from or incurred in connection with any:`,
        subClauses: [
          { text: `damage, loss, injury or death to the extent caused or contributed to by the act, omission, negligence or default of the Hirer, the Hirer’s Employees and Agents or the Hirer’s Invitees;` },
          { text: `BlueFit doing anything the Licensor is permitted or obliged to do under this agreement;` },
          { text: `any Service not being available, interrupted or broken down or any part of BlueFit’s plant and equipment being broken down,` },
          { text: `except to the extent that the damage, loss or injury is caused by the act, omission, negligence or default of the Licensor, the Licensor’s Employees and Agents. The hirer’s liability will be proportionally reduced to the extent damage, loss or injury was directly caused by an act, omission, negligence or default of BlueFit, its Employees or Agents.` }
        ]
      }
    ]
  },
  {
    title: "Admission and Removal of Persons",
    clauses: [
      {
        text: `The Centre Manager may at any time, in their absolute discretion:`,
        subClauses: [
          { text: `Refuse admission of any person;` },
          { text: `Direct any person or persons to leave.` }
        ]
      }
    ]
  },
  {
    title: "Closure of the \"Facility Name\" Facilities",
    clauses: [
      {
        text: `If:`,
        subClauses: [
          { text: `A person who has been refused entry re-enters; or` },
          { text: `A person directed to leave fails to do so, the Centre Manager may close the Facilities.` }
        ]
      },
      {
        text: `If the Centre Manager closes the Facilities in consequence of 14.1:`,
        subClauses: [
          { text: `The Hirer will be deemed to have voluntarily abandoned the Hire;` },
          { text: `The Hirer will be charged for the entire hire period; and` },
          { text: `BlueFit will not be liable for any loss or damage sustained as a result of closure.` }
        ]
      },
      { text: `The Centre Manager may at any time, in their absolute discretion, close the Facilities to ensure the safety of those present.` }
    ]
  },
  {
    title: "Relationship between Parties",
    clauses: [
      { text: `The hirer acknowledges that nothing in this agreement, or any other applicable terms and conditions gives rise to a relationship of landlord and tenant, and that the hirer occupies the Facilities, for the Hiring Purpose and Hiring Period as a licensee.` },
      { text: `This agreement grants non-exclusive licence rights to the Hirer and does not confer any exclusive use right of occupancy on the Hirer.` },
      { text: `The rights conferred on the Hirer in this agreement are personal to the Hirer and do not create or confer upon Hirer any tenancy or estate or interest in some or all of the "Facility Name" or Facilities.` },
      { text: `The Hirer acknowledges and agrees that legal possession and control remains at all times in the "Facility Name" and Bluefit and this agreement does not operate as a demise or constitute any contract of tenancy.` }
    ]
  },
  {
    title: "Dispute Resolution",
    clauses: [
      { text: `If a dispute arises between the parties in relation to this Agreement, the dispute must be dealt with in accordance with this clause.` },
      { text: `Any party claiming that a dispute exists must notify the other party to the dispute (Second Party) in writing of the nature of the dispute.` },
      { text: `If the dispute is not resolved by agreement within five working days of the Second Party receiving the notice referred to in clause 16(b) above, either party may refer the matter to mediation conducted by a mediator agreed between the parties within a further five working days or failing agreement within that period, as appointed by the executive director for the time being of the Australian Commercial Disputes Centre Limited. The costs of the mediator must be borne equally between the disputing parties. The chosen mediator must determine the procedures for the mediation. The chosen mediator will not have the power or authority to make any other determination in relation to the dispute.` },
      { text: `If the parties have not mediated a resolution of the dispute within 20 working days of the selection of a mediator, neither party must be obliged to continue any attempt at mediation under this clause, and either party may then commence such legal proceedings as it thinks fit in relation to the dispute.` }
    ]
  },
  {
    title: "Governing Law",
    clauses: [
      { text: `This Agreement will be governed by the laws of the State in which the Centre is located.` }
    ]
  }
];



